/*
 * The EaseFlow page's setup section (sections/easeflow-setup.liquid).
 * Three steps play as one story on a small desktop: the order page's
 * download button for the visitor's own system downloads the installer;
 * the installer (macOS Installer on a Mac, Windows setup elsewhere) puts
 * the EaseFlow app and the Premiere Pro plugin in place; then in Premiere
 * Pro a click on a preset in the EaseFlow panel eases the two keyframes:
 * the straight line in Effect Controls becomes the curve, with EaseFlow's
 * keyframes along it, and the clip moves with that easing. The step cards
 * light up in time and jump to their step when clicked.
 *
 * Plays only while on screen and the tab is visible; leaving stops it and
 * it starts over next time. With reduced motion (or without script) each
 * step shows as a finished still, and the cards switch between them.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const STOP = {};
  const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || navigator.userAgent;
  const isMac = /mac|iphone|ipad|ipod/i.test(platform);
  const WIN = { '⌘': 'Ctrl', '⌃': 'Ctrl', '⌥': 'Alt', '⇧': 'Shift' };
  // Running text: "press ⌘Z" becomes "press Ctrl+Z" off a Mac.
  const forOs = (text) =>
    isMac ? text : text.replace(/([⌃⌥⇧⌘]+)(\S)/g, (m, mods, key) => [...mods].map((c) => WIN[c]).concat(key).join('+'));
  // How long each step plays, in ms (the waits in its script add up to this).
  const LENGTH = { 1: 5450, 2: 5700, 3: 5850 };

  const init = (root) => {
    if (root.dataset.esSetupReady) return;
    root.dataset.esSetupReady = 'true';

    const q = (sel) => root.querySelector(sel);
    const stage = q('[data-es-stage]');
    if (!stage) return;
    const web = q('[data-es-web]');
    const primary = q('[data-es-primary]');
    const primaryLabel = q('[data-es-primary-label]');
    const file = q('[data-es-file]');
    const pr = q('[data-es-pr]');
    const preset = q('[data-es-preset]');
    const field = q('[data-es-field]');
    const cursor = q('[data-es-cursor]');
    const cards = [...root.querySelectorAll('[data-es-card]')];
    const buttons = [...root.querySelectorAll('[data-es-go]')];
    const fills = cards.map((card) => card.querySelector('.es-step-fill'));
    const scenes = [...stage.querySelectorAll('.es-scene')];
    if (!web || !pr || !preset || !field || !cursor || scenes.length !== 3) return;

    // This visitor's system: its installer and its window chrome.
    stage.classList.toggle('is-mac', isMac);
    stage.classList.toggle('is-win', !isMac);
    const inst = stage.querySelector(isMac ? '.es-inst.es-only-mac' : '.es-inst.es-only-win');
    const installBtn = inst && inst.querySelector('[data-es-go-btn]');
    const macSteps = [...stage.querySelectorAll('[data-es-m]')];
    const d = root.dataset;
    const ownLabel = isMac ? d.macLabel : d.winLabel;
    const ext = q('[data-es-ext]');
    if (ext) ext.textContent = isMac ? 'PKG' : 'EXE';
    root.querySelectorAll('[data-es-os]').forEach((el) => (el.textContent = forOs(el.textContent)));
    const curve = d.curve || field.textContent;

    const setStep = (n) => {
      stage.dataset.step = String(n);
      cards.forEach((card, i) => {
        const on = i + 1 === n;
        card.classList.toggle('is-current', on);
        if (on) buttons[i].setAttribute('aria-current', 'step');
        else buttons[i].removeAttribute('aria-current');
      });
    };

    const fillCard = (n, ms) => {
      fills.forEach((fill, i) => {
        if (!fill) return;
        fill.style.transition = 'none';
        fill.style.transform = 'scaleX(0)';
        if (i + 1 === n && ms) {
          void fill.offsetWidth;
          fill.style.transition = `transform ${ms}ms linear`;
          fill.style.transform = 'scaleX(1)';
        }
      });
    };

    const setMac = (state) => macSteps.forEach((li) => li.classList.toggle('is-now', li.dataset.esM === state));

    // Each scene's details, at their start (snapped, no animation).
    const snap = (el, fn) => {
      el.classList.add('is-instant');
      fn();
      void el.offsetWidth;
      el.classList.remove('is-instant');
    };
    const resetWeb = () =>
      snap(web, () => {
        web.classList.remove('is-down', 'is-got', 'is-open');
        primaryLabel.textContent = ownLabel;
      });
    const resetInst = () =>
      snap(inst, () => {
        inst.classList.remove('is-run', 'is-app', 'is-int', 'is-done');
        setMac('ready');
      });
    const resetPr = () =>
      snap(pr, () => {
        pr.classList.remove('is-eased', 'is-play');
        field.textContent = '0, 0, 1, 1';
      });

    // The finished still of each step.
    const doneWeb = () =>
      snap(web, () => {
        web.classList.remove('is-down', 'is-open');
        web.classList.add('is-got');
        primaryLabel.textContent = d.downloaded;
      });
    const doneInst = () =>
      snap(inst, () => {
        inst.classList.add('is-run', 'is-app', 'is-int', 'is-done');
        setMac('done');
      });
    const donePr = () =>
      snap(pr, () => {
        pr.classList.add('is-eased');
        pr.classList.remove('is-play');
        field.textContent = curve;
      });

    let run = 0;
    let playing = false;
    let inView = false;

    const wait = (r, ms) =>
      new Promise((resolve, reject) => {
        window.setTimeout(() => (r === run ? resolve() : reject(STOP)), ms);
      });

    // Where an element sits inside the stage, in px.
    const spot = (el, fx = 0.5, fy = 0.5) => {
      const s = stage.getBoundingClientRect();
      const b = el.getBoundingClientRect();
      return [b.left - s.left + b.width * fx, b.top - s.top + b.height * fy];
    };
    const cursorAt = (x, y) => {
      cursor.classList.add('is-jump');
      cursor.style.setProperty('--cx', `${x}px`);
      cursor.style.setProperty('--cy', `${y}px`);
      void cursor.offsetWidth;
      cursor.classList.remove('is-jump');
    };
    const cursorIn = () => {
      const s = stage.getBoundingClientRect();
      cursorAt(s.width * 0.82, s.height * 0.92);
      cursor.classList.add('is-on');
    };
    const moveTo = (r, el, fx, fy, ms = 650) => {
      const [x, y] = spot(el, fx, fy);
      cursor.style.setProperty('--cm', `${ms}ms`);
      cursor.style.setProperty('--cx', `${x}px`);
      cursor.style.setProperty('--cy', `${y}px`);
      return wait(r, ms);
    };
    const tap = (r) => {
      cursor.classList.remove('is-click');
      void cursor.offsetWidth;
      cursor.classList.add('is-click');
      return wait(r, 200);
    };
    // ---- 1. Download ----
    const step1 = async (r) => {
      resetWeb();
      setStep(1);
      fillCard(1, LENGTH[1]);
      await wait(r, 700);
      cursorIn();
      await moveTo(r, primary, 0.5, 0.62, 800);
      await tap(r);
      primaryLabel.textContent = d.downloading;
      web.classList.add('is-down');
      await wait(r, 1700);
      web.classList.remove('is-down');
      web.classList.add('is-got');
      primaryLabel.textContent = d.downloaded;
      await wait(r, 700);
      await moveTo(r, file, 0.45, 0.55, 700);
      await tap(r);
      web.classList.add('is-open');
      await wait(r, 450);
    };

    // ---- 2. Install ----
    const step2 = async (r) => {
      resetInst();
      setStep(2);
      fillCard(2, LENGTH[2]);
      // the pointer carries on from the download it just opened
      await wait(r, 700);
      if (!cursor.classList.contains('is-on')) cursorIn();
      await moveTo(r, installBtn, 0.5, 0.6, 700);
      await tap(r);
      inst.classList.add('is-run');
      setMac('run');
      await wait(r, 950);
      inst.classList.add('is-app');
      await wait(r, 1150);
      inst.classList.add('is-int');
      await wait(r, 450);
      inst.classList.add('is-done');
      setMac('done');
      cursor.classList.remove('is-on');
      await wait(r, 1550);
    };

    // ---- 3. Click a preset in the EaseFlow panel ----
    const step3 = async (r) => {
      resetPr();
      setStep(3);
      fillCard(3, LENGTH[3]);
      await wait(r, 600);
      cursorIn();
      await moveTo(r, preset, 0.5, 0.42, 750);
      await tap(r);
      // the preset loads into the graph and eases the keyframes at the playhead
      pr.classList.add('is-eased');
      field.textContent = curve;
      await wait(r, 500);
      cursor.classList.remove('is-on');
      await wait(r, 400);
      // and the clip moves with the new easing
      pr.classList.add('is-play');
      await wait(r, 3400);
    };

    const STEPS = [step1, step2, step3];

    const play = async (from = 1) => {
      run += 1;
      const r = run;
      playing = true;
      let n = from;
      try {
        for (;;) {
          await STEPS[n - 1](r);
          n = n === 3 ? 1 : n + 1;
        }
      } catch (error) {
        if (error !== STOP) throw error;
      }
    };

    // Back to the very start, ready for the next time it is seen.
    const stop = () => {
      run += 1;
      playing = false;
      cursor.classList.remove('is-on');
      resetWeb();
      resetInst();
      resetPr();
      fillCard(0);
      setStep(1);
    };

    // Stills: every step finished, the chosen one showing.
    const showStill = (n) => {
      doneWeb();
      doneInst();
      donePr();
      fillCard(0);
      setStep(n);
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const n = Number(button.dataset.esGo) || 1;
        if (!playing) {
          showStill(n);
          return;
        }
        cursor.classList.remove('is-on');
        play(n);
      });
    });

    if (still.matches || !('IntersectionObserver' in window)) {
      showStill(3);
      return;
    }

    stop();

    const update = () => {
      const go = inView && !document.hidden;
      if (go && !playing) play(1);
      else if (!go && playing) stop();
    };

    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          inView = entry.isIntersecting;
        });
        update();
      },
      { threshold: 0.35 }
    ).observe(stage);
    document.addEventListener('visibilitychange', update);
  };

  const boot = () => document.querySelectorAll('[data-easeflow-setup]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
